import { useState, useEffect, useCallback, useRef } from 'react';
import { tenantUrl } from '../utils/tenantPath';
import { useParams, useNavigate, Link } from '../routing';
import { dishesApi, suppliersApi, catalogApi, formatCurrency, formatDishCurrency } from '../utils/api';
import { toast } from '../components/Toast';
import SearchSelect from '../components/SearchSelect';
import ConversionFields from '../components/ConversionFields';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import StatCardsRow from '../components/StatCardsRow';
import { useIsMobile } from '../utils/useIsMobile';
import { formatDateShort } from '../utils/dateFormat';
import { useConfirm } from '../utils/useConfirm';
import { track, EVENTS } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import HqOnlyNotice, { HqOnlyLock } from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';

const UNIT_LABEL = { gram: 'גרם', kg: 'ק"ג', ml: 'מ"ל', unit: 'יח׳' };
const fmtPct = (p) => (p == null ? '-' : `${(p * 100).toFixed(1)}%`);
// Hebrew counts: "1 מרכיבים" is wrong, and this number is 1 often enough on a
// small dish to be worth the two lines.
// Says what the ordering is, because rankedIngredients re-sorts on every load
// and a quantity edit triggers one — so a row can move out from under the
// cursor that just edited it.
const SORT_NOTE = 'מסודר מהיקר לזול · מרכיבים ללא תמחור ראשונים';

// Module scope on purpose. Declared inside DishDetailPage it was a NEW component
// type on every render, so React unmounted and rebuilt each share cell instead
// of updating it — invisible today (the cell is stateless markup) and a trap the
// moment anyone gives .dish-share-fill a transition, since the element it would
// animate is destroyed each time.
function ShareCell({ ing, total }) {
  if (ing.needs_costing || ing.price_unknown) return <span className="dish-share-none">לא נספר</span>;
  const t = Number(total) || 0;
  if (t <= 0) return <span className="dish-share-none">-</span>;
  const share = (Number(ing.cost) || 0) / t;
  return (
    <span className="dish-share">
      <span className="dish-share-track" aria-hidden="true">
        <span className="dish-share-fill" style={{ width: `${Math.min(100, share * 100)}%` }} />
      </span>
      <span className="dish-share-pct">{(share * 100).toFixed(0)}%</span>
    </span>
  );
}

const ingredientCount = (n) => (n === 1 ? 'מרכיב אחד' : `${n} מרכיבים`);
const fmtQty = (q, unit) => `${(Number(q) || 0).toLocaleString('he-IL', { maximumFractionDigits: 1 })} ${UNIT_LABEL[unit] || ''}`;

// How each figure is calculated, in the voice /suppliers uses for סה"כ רכש:
// plain words, no formulas, no doc-type names. Each one answers "what is this
// number" first and "what moves it" second.
const COST_INFO = (
  <>
    כמה עולים חומרי הגלם של מנה אחת.
    <ul className="stat-info-list">
      <li>מחברים את העלות של כל המרכיבים במנה</li>
      <li>עלות מרכיב = המחיר האחרון שקניתם, לפי הכמות שנכנסת למנה</li>
      <li>פחת מייקר את המרכיב - צריך לקנות יותר כדי שיישאר מה שהמתכון דורש</li>
    </ul>
    <p className="stat-info-note">
      מרכיב בלי המרה או בלי מחיר נספר כאפס, ולכן הסכום שמוצג נמוך מהעלות האמיתית.
    </p>
  </>
);

const FOOD_COST_INFO = (
  <>
    איזה חלק ממחיר המנה בתפריט הולך על חומרי הגלם.
    <ul className="stat-info-list">
      <li>עלות המנה חלקי המחיר בתפריט</li>
      <li>ככל שהאחוז נמוך יותר - נשאר לכם יותר מכל מנה שנמכרת</li>
      <li>מה שנשאר הוא הרווח הגולמי, לפני שכר, שכירות וכל השאר</li>
    </ul>
    <p className="stat-info-note">
      אין ״אחוז נכון״ אחד - הוא משתנה לפי סוג המסעדה והמנה.
    </p>
  </>
);

const WOLT_INFO = (
  <>
    אותו חישוב, מול המחיר שאתם גובים בוולט.
    <ul className="stat-info-list">
      <li>חומרי הגלם עולים בדיוק אותו דבר</li>
      <li>המחיר בוולט בדרך כלל גבוה יותר, ולכן האחוז יוצא נמוך יותר</li>
      <li>עמלת וולט יורדת מהרווח, לא מהעלות</li>
    </ul>
    <p className="stat-info-note">
      אחוז נמוך יותר בוולט לא אומר שהמנה רווחית יותר שם - צריך להסתכל על הרווח אחרי העמלה.
    </p>
  </>
);

const WEIGHT_INFO = (
  <>
    כמה שוקלים כל המרכיבים של המנה יחד.
    <ul className="stat-info-list">
      <li>מחברים את הכמויות של המרכיבים שנמדדים במשקל</li>
      <li>מוצרים שנספרים ביחידות (ביצה, לחמנייה) לא נכללים - אין להם משקל</li>
    </ul>
  </>
);

export default function DishDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { restaurantId } = useAuth();
  const [askConfirm, confirmDialog] = useConfirm();
  const [dish, setDish] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // The title edits in place; the page's actions live in one ⋮ menu.
  const [editingName, setEditingName] = useState(false);
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  // Which ingredient row's ⋮ is open (ingredient id), or null.
  const [ingMenuFor, setIngMenuFor] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // header edit buffers
  const [name, setName] = useState('');
  const [menuPrice, setMenuPrice] = useState('');
  const [woltPrice, setWoltPrice] = useState('');
  const [woltPct, setWoltPct] = useState('');

  // add-ingredient modal
  const [showAdd, setShowAdd] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [qtyUsed, setQtyUsed] = useState('');
  const [saving, setSaving] = useState(false);
  // The picked product's EXISTING conversion (null = it has none yet, which is
  // what makes the modal ask for one instead of just showing it).
  const [addCosting, setAddCosting] = useState(null);
  const [addBaseUnit, setAddBaseUnit] = useState('gram');
  const [addPackQty, setAddPackQty] = useState('');
  const [addWastePct, setAddWastePct] = useState('');
  // A FAILED lookup is not the same as "no conversion yet" (the endpoint returns
  // 200 null for that). Treating them alike would let the add modal offer to
  // create a conversion for a product that already has one, and PUT would then
  // overwrite it — pack_label included — for every dish using that product.
  const [addCostingError, setAddCostingError] = useState(false);
  // True only while a getCosting lookup is in flight. Without this, the render
  // state right after picking a product (productId set, addCosting still null,
  // addCostingError still false) is indistinguishable from "confirmed no
  // conversion" — which would flash the editable create-form, in front of a
  // conversion that in fact already exists, for as long as the request takes.
  const [addCostingLoading, setAddCostingLoading] = useState(false);
  // Token guard against out-of-order getCosting responses. Picking product A
  // then quickly picking B (or closing and reopening the modal) can put two
  // lookups in flight — without this, the SLOWER one wins whenever it lands
  // last and repopulates addCosting for a product that is no longer selected.
  const addCostingToken = useRef(0);

  // conversion ("המרה") modal — base unit, pack qty, and waste % (waste lives on
  // the product's conversion: it grosses up the product's price).
  const [costingRow, setCostingRow] = useState(null);
  const [packQty, setPackQty] = useState('');
  const [baseUnit, setBaseUnit] = useState('gram');
  const [costWastePct, setCostWastePct] = useState('');
  const [packLabel, setPackLabel] = useState('');
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dishesApi.getOne(id);
      setDish(res.data);
      setName(res.data.name);
      setMenuPrice(res.data.menu_price ? String(res.data.menu_price) : '');
      setWoltPrice(res.data.wolt_price ? String(res.data.wolt_price) : '');
      setWoltPct(res.data.wolt_pct ? String(res.data.wolt_pct) : '');
      setLoadError(false);
    } catch {
      // Not a toast-and-redirect: bouncing to /dishes on a failed fetch throws
      // away the URL the user was on and reads as "this dish is gone" when the
      // truth is "the request failed". Stay put, say so, offer the retry.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Persist header fields on blur, only when changed.
  const saveHeader = async (patch) => {
    try {
      await dishesApi.update(id, patch);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בשמירה', 'error');
    }
  };
  // Leaves edit mode either way; an empty or unchanged name simply restores.
  const commitName = () => {
    setEditingName(false);
    const v = name.trim();
    if (!v || v === dish.name) { setName(dish.name); return; }
    saveHeader({ name: v });
  };
  const onPriceBlur = () => {
    const v = parseFloat(menuPrice) || 0;
    if (v === (dish.menu_price || 0)) return;
    saveHeader({ menu_price: v });
  };
  const onWoltPriceBlur = () => {
    const v = parseFloat(woltPrice) || 0;
    if (v === (dish.wolt_price || 0)) return;
    saveHeader({ wolt_price: v });
  };
  const onWoltPctBlur = () => {
    const v = parseFloat(woltPct) || 0;
    if (v === (dish.wolt_pct || 0)) return;
    saveHeader({ wolt_pct: v });
  };

  // ── add ingredient ───────────────────────────────────────────────────────
  // Bumping the token here — and everywhere else the add-costing fields are
  // reset — is what lets onPickProduct's response tell a stale lookup from a
  // current one. Centralized so a future reset site can't forget the bump.
  const resetAddCosting = () => {
    const token = ++addCostingToken.current;
    setAddCosting(null); setAddBaseUnit('gram'); setAddPackQty(''); setAddWastePct('');
    setAddCostingError(false); setAddCostingLoading(false);
    return token;
  };

  const openAdd = async () => {
    setShowAdd(true);
    setSupplierId(''); setProductId(''); setProducts([]); setQtyUsed('');
    resetAddCosting();
    if (suppliers.length === 0) {
      try {
        const res = await suppliersApi.getAll({ include_empty: true });
        setSuppliers(res.data || []);
      } catch { toast('שגיאה בטעינת ספקים', 'error'); }
    }
  };

  // Closing must invalidate any in-flight getCosting: otherwise its failure
  // toast lands on a screen with no add modal on it.
  const closeAdd = () => { setShowAdd(false); resetAddCosting(); };

  const onPickSupplier = async (sid) => {
    setSupplierId(sid);
    setProductId(''); setProducts([]);
    resetAddCosting();
    if (!sid) return;
    try {
      const res = await catalogApi.getBySupplier(sid);
      setProducts(res.data || []);
    } catch { toast('שגיאה בטעינת מוצרים', 'error'); }
  };

  // A FAILED getCosting lookup is distinct from "no conversion yet" (see
  // addCostingError above) — so on error we flag it and leave addCosting null,
  // rather than silently treating a failure as "this product has none".
  const onPickProduct = async (pid) => {
    setProductId(pid);
    const token = resetAddCosting();
    if (!pid) return;
    setAddCostingLoading(true);
    try {
      const res = await dishesApi.getCosting(pid);
      if (token !== addCostingToken.current) return;   // superseded
      setAddCosting(res.data || null);
      setAddCostingLoading(false);
    } catch {
      if (token !== addCostingToken.current) return;
      setAddCostingError(true);
      setAddCostingLoading(false);
      toast('לא ניתן לטעון את ההמרה של המוצר', 'error');
    }
  };

  // Which unit the quantity field is speaking. An existing conversion wins; a
  // new one follows the checkbox live, which is the whole point of the block.
  // A failed lookup falls back to grams — the modal shows no conversion block
  // in that case, so there is no checkbox to follow.
  const addUnit = addCosting ? (addCosting.base_unit || 'gram') : addBaseUnit;

  const handleAddIngredient = async (e) => {
    e.preventDefault();
    if (!productId) { toast('יש לבחור מוצר', 'error'); return; }
    const packQtyNum = parseFloat(addPackQty) || 0;
    // Leaving the conversion block empty is allowed (the row then shows
    // "הגדר המרה"). A block that was TOUCHED — a pack size OR a waste % typed in
    // — but does not resolve to a usable pack size is a typo, not an intent to
    // skip the conversion; the server would 400 on pack_qty anyway, and silently
    // dropping a typed waste % with no message is its own bug.
    if (!addCosting && !addCostingError &&
        (addPackQty.trim() !== '' || addWastePct.trim() !== '') && packQtyNum <= 0) {
      toast('יש להזין כמות אריזה גדולה מ-0', 'error');
      return;
    }
    setSaving(true);
    try {
      // Conversion FIRST: it belongs to the product, so it stays correct even if
      // adding the ingredient then fails. Only sent for a product KNOWN to have
      // none — setCosting replaces the whole row, so sending it for an existing
      // conversion would blank that product's pack_label and waste. addCostingError
      // means we could not find out, and addCostingLoading means we don't know YET
      // — either way, guessing "none" is what would destroy a real conversion
      // across every dish that uses the product.
      if (!addCostingLoading && !addCosting && !addCostingError && packQtyNum > 0) {
        const wastePctNum = parseFloat(addWastePct) || 0;
        await dishesApi.setCosting(productId, {
          base_unit: addBaseUnit,
          pack_qty: packQtyNum,
          waste_pct: wastePctNum,
        });
        // Reflect what was just saved: if addIngredient below then throws, the
        // modal stays open, and without this it would still claim "no
        // conversion yet" for a product that now has one.
        setAddCosting({ base_unit: addBaseUnit, pack_qty: packQtyNum, waste_pct: wastePctNum, pack_label: '' });
      }
      await dishesApi.addIngredient(id, {
        product_catalog_id: productId,
        qty_used: qtyUsed,
      });
      // Guarded: these routes deliberately let an admin with NO restaurant
      // selected through (getOwnedDish / productInScope), so restaurantId can be
      // null here. String(null) would file the event under a phantom "null"
      // tenant, and the PostHog super properties still hold whichever restaurant
      // was selected earlier — so an unguarded event does not merely lose the
      // tenant, it names the WRONG one. An unattributable event is worth less
      // than no event in a per-restaurant adoption metric.
      if (restaurantId != null) track(EVENTS.DISH_INGREDIENT_ADDED, {
        restaurant_id: String(restaurantId),
        // Did this product already have a conversion, or did the user have to
        // supply one to get here? The ratio is how much setup friction is left.
        conversion_existed: !!addCosting,
      });
      closeAdd();
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בהוספת המרכיב', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── inline edit qty / waste ──────────────────────────────────────────────
  const saveIngredient = async (ing, patch) => {
    try {
      await dishesApi.updateIngredient(id, ing.id, patch);
      load();
    } catch { toast('שגיאה בעדכון המרכיב', 'error'); }
  };

  const removeIngredient = (ing) => askConfirm({
    title: 'הסרת מרכיב',
    message: `להסיר את "${ing.product_name}" מהמנה?`,
    detail: 'ההמרה של המוצר נשמרת ותמשיך לשמש מנות אחרות.',
    confirmLabel: 'הסר את המרכיב',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await dishesApi.deleteIngredient(id, ing.id);
        load();
      } catch { toast('שגיאה בהסרת המרכיב', 'error'); }
    },
  });

  // The dish's own delete lives here because the mobile list has none: a
  // ListRow is a <button>, so a per-row delete cannot nest inside it, and the
  // app's mobile idiom (DocumentsPage) is that the row navigates and the detail
  // view owns destructive actions.
  const removeDish = () => askConfirm({
    title: 'מחיקת מנה',
    message: `למחוק את המנה "${dish.name}"?`,
    detail: dish.ingredients.length > 0 ? `${dish.ingredients.length} מרכיבים יימחקו יחד איתה.` : undefined,
    confirmLabel: 'מחק את המנה',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await dishesApi.delete(dish.id);
        toast('המנה נמחקה');
        navigate('/dishes');
      } catch { toast('שגיאה במחיקת המנה', 'error'); }
    },
  });

  // ── conversion modal ─────────────────────────────────────────────────────
  const openCosting = async (ing) => {
    setCostingRow(ing);
    setPackQty(ing.pack_qty ? String(ing.pack_qty) : '');
    setBaseUnit(ing.base_unit || 'gram');
    setCostWastePct(ing.waste_pct ? String(ing.waste_pct) : '');
    setPackLabel(ing.pack_label || '');
    setPurchases([]); setLoadingPurchases(true);
    try {
      const res = await dishesApi.getProductPurchases(ing.product_catalog_id);
      setPurchases(Array.isArray(res.data) ? res.data : []);
    } catch { /* purchase history is a helper - non-fatal if it fails */ }
    finally { setLoadingPurchases(false); }
  };
  const handleSaveCosting = async (e) => {
    e.preventDefault();
    const q = parseFloat(packQty) || 0;
    if (q <= 0) { toast('יש להזין כמות אריזה גדולה מ-0', 'error'); return; }
    setSaving(true);
    try {
      await dishesApi.setCosting(costingRow.product_catalog_id, {
        base_unit: baseUnit, pack_qty: q,
        waste_pct: parseFloat(costWastePct) || 0,
        pack_label: packLabel.trim(),
      });
      setCostingRow(null);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בשמירת ההמרה', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>;
  }
  if (loadError || !dish) {
    return <InlineLoadError message="שגיאה בטעינת המנה" onRetry={load} />;
  }

  const numCell = (ing) => ing.needs_costing
    ? <span className="badge badge-yellow">חסרה המרה</span>
    : ing.price_unknown
      ? <span className="badge badge-gray">אין מחיר</span>
      : null;

  // Dish weight: every weighed ingredient is in grams now, so this is a plain
  // sum. Counted products ('unit') have no weight to add — they're excluded and
  // flagged, so the number is never read as the dish's full weight.
  const gramsTotal = dish.ingredients
    .filter(i => i.base_unit !== 'unit')
    .reduce((sum, i) => sum + (Number(i.qty_used) || 0), 0);
  const countedIngredients = dish.ingredients.filter(i => i.base_unit === 'unit').length;
  // Same definition as the list DTO's incomplete_count, computed from the
  // per-ingredient flags the detail endpoint already sends.
  const incompleteCount = dish.ingredients.filter(i => i.needs_costing || i.price_unknown).length;
  // An empty dish is unmeasured for the same reason: cost 0 makes food cost
  // 0.0% and gross profit the whole menu price, which is the most flattering
  // reading possible of a dish nobody has costed yet.
  const noVerdict = incompleteCount > 0 || dish.ingredients.length === 0;
  const hasIngredients = dish.ingredients.length > 0;

  // Ordered by what it COSTS. "עלות מזון 45%" is a verdict, and this ranking is
  // the only thing on the page that says what to do about it — which ingredient
  // to renegotiate, substitute, or use less of. It shipped in insertion order:
  // the order things happened to be typed, the one ordering carrying no
  // information at all.
  //
  // Unmeasured ingredients sort FIRST, not last. Their cost is 0, so plain
  // cost-descending buries them at the bottom as though they mattered least,
  // when they are the only rows blocking every other number here from being
  // true. They are the work, so they go where the eye starts.
  //
  // The share denominator is the dish TOTAL — unlike the category bars, which
  // normalise to the largest row because a share-of-revenue there would draw
  // every category as a stub. Here the parts genuinely are parts of one whole
  // and sum to 100%, and a handful of ingredients means no row collapses.
  const rankedIngredients = [...dish.ingredients].sort((a, b) => {
    const aBlocked = a.needs_costing || a.price_unknown;
    const bBlocked = b.needs_costing || b.price_unknown;
    if (aBlocked !== bBlocked) return aBlocked ? -1 : 1;
    return (Number(b.cost) || 0) - (Number(a.cost) || 0);
  });



  return (
    <div>
      <div className="breadcrumb">
        <Link className="link" to="/dishes">עץ מוצר</Link>
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        {dish.name}
      </div>

      {/* The header answers two different questions and they are now two
          different surfaces. Above: what YOU set - name, and a price per sales
          channel. Below: what we COMPUTED from it. They used to be one card at
          one weight, so a field you type into and a figure you read sat side by
          side looking equally authored. */}
      {/* Title row. The dish's NAME is the page's subject, so it reads as the
          page title and edits in place - it was a permanent text input, which
          made the thing you are looking at look like a form field you had not
          filled in yet. */}
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title dish-title">
            {/* Named, because the title is a value the user typed and can retype.
                A dish called "jhdskds" or "1233" is indistinguishable from a page
                heading that has gone wrong - the label says which of the two it
                is, and what the pencil beside it edits. */}
            <span className="dish-title-label">שם מנה:</span>
            {editingName ? (
              <input
                className="form-control dish-title-input"
                autoFocus
                value={name}
                onChange={e => setName(e.target.value)}
                onBlur={commitName}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                  // Escape restores the stored name — an edit you back out of must
                  // not be the same gesture as an edit you confirm.
                  if (e.key === 'Escape') { setName(dish.name); setEditingName(false); }
                }}
                aria-label="שם המנה"
              />
            ) : (
              <>
                <span>{dish.name}</span>
                <button type="button" className="icon-btn" aria-label="שנה את שם המנה"
                  onClick={() => setEditingName(true)}>
                  <Icon name="edit" size={16} />
                </button>
              </>
            )}
          </h1>
          {dish.hidden_hq_cost > 0 && (
            <HqOnlyNotice
              message={`המנה כוללת רכיבים מספקים מוסתרים — מנהלים לא רואים אותם, ולכן העלות שהם רואים נמוכה ב-${formatDishCurrency(dish.hidden_hq_cost)}.`}
            />
          )}
        </div>
        <div className="page-title-actions">
          <div className="page-menu">
            <button
              type="button"
              className="page-menu-trigger"
              aria-label="פעולות נוספות"
              aria-expanded={pageMenuOpen}
              onClick={() => setPageMenuOpen(o => !o)}
            >⋮</button>
            {pageMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                <div className="page-menu-panel" style={{ minWidth: 190 }}>
                  <button type="button" className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); openAdd(); }}>
                    <Icon name="plus" size={15} /> הוסף מרכיב
                  </button>
                  <button type="button" className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); setShowHelp(true); }}>
                    <Icon name="info" size={15} /> חשוב לדעת
                  </button>
                  <button type="button" className="page-menu-item" style={{ color: '#b91c1c' }}
                    onClick={() => { setPageMenuOpen(false); removeDish(); }}>
                    <Icon name="trash" size={15} /> מחק מנה
                  </button>
                  <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Same rule as the list screen, and it has to hold here too: an
          ingredient with no conversion or no known price costs 0, so on an
          incomplete dish the cost is a FLOOR and every figure derived from it
          flatters. */}
      {incompleteCount > 0 && (
        <div className="dish-incomplete-note" role="status">
          <Icon name="alert" size={16} />
          <span>
            <strong>{ingredientCount(incompleteCount)} מתוך {dish.ingredients.length} עדיין ללא תמחור.</strong>{' '}
            העלות האמיתית גבוהה יותר, ולכן אחוז עלות המזון והרווח אינם מוצגים.
          </span>
        </div>
      )}

      {/* The answer comes FIRST. You open a dish to find out what it costs and
          what it earns; the prices that feed those figures are settings you
          change occasionally, so they sit under the result, not above it.
          
          A dish with NO ingredients gets no strip at all. Four cards reading
          "-" is a wall of nothing where the answer should be, and it pushes the
          one thing the page needs - add an ingredient - below the fold on a
          phone. There is no measurement yet, so the page says so once, in the
          empty state, instead of four times in a grid. */}
      {hasIngredients && (
      <StatCardsRow
        cards={[
          {
            key: 'cost',
            value: formatDishCurrency(dish.total_cost),
            label: 'עלות מנה',
            info: COST_INFO,
            sub: incompleteCount > 0 ? `לא כולל ${ingredientCount(incompleteCount)} ללא תמחור` : null,
          },
          {
            key: 'foodcost',
            value: noVerdict ? '-' : fmtPct(dish.food_cost_pct),
            label: 'עלות מזון % - תפריט',
            info: FOOD_COST_INFO,
            sub: noVerdict
              ? 'ידרוש תמחור מלא'
              : `רווח גולמי ${dish.gross_profit == null ? '-' : formatDishCurrency(dish.gross_profit)}`,
          },
          {
            key: 'wolt',
            value: noVerdict ? '-' : fmtPct(dish.wolt_food_cost_pct),
            label: 'עלות מזון % - וולט',
            info: WOLT_INFO,
            sub: noVerdict
              ? 'ידרוש תמחור מלא'
              : dish.wolt_gross_profit == null
                ? 'לא הוגדר מחיר וולט'
                : `רווח גולמי ${formatDishCurrency(dish.wolt_gross_profit)}`,
          },
          {
            key: 'weight',
            value: gramsTotal > 0 ? `${gramsTotal.toLocaleString('he-IL', { maximumFractionDigits: 0 })} גרם` : '-',
            label: 'משקל מנה',
            info: WEIGHT_INFO,
            sub: countedIngredients > 0 ? `+ ${countedIngredients} נספרים ביח׳` : null,
          },
        ]}
      />
      )}

      {/* The add action lives in TWO places on purpose: here, where it is the
          page's primary job and has to be visible, and in the ⋮ menu, which is
          where a keyboard or one-handed user reaches for actions. A CTA behind a
          menu is a CTA nobody presses. */}
      {/* Three short prices on one line, not a card of stacked form rows. They
          are the inputs to the figures above - a strip of settings, so they get
          a strip's worth of height. */}
      <div className="dish-prices">
        <span className="dish-prices-title">מחירי מכירה</span>
        <label className="dish-price-field">
          <span>בתפריט (₪)</span>
          <input className="form-control input-money" type="number" min="0" step="0.01" inputMode="decimal"
            style={{ direction: 'ltr', textAlign: 'right' }}
            value={menuPrice} onChange={e => setMenuPrice(e.target.value)} onBlur={onPriceBlur} placeholder="-" />
        </label>
        <label className="dish-price-field">
          <span>בוולט (₪)</span>
          <input className="form-control input-money" type="number" min="0" step="0.01" inputMode="decimal"
            style={{ direction: 'ltr', textAlign: 'right' }}
            value={woltPrice} onChange={e => setWoltPrice(e.target.value)} onBlur={onWoltPriceBlur} placeholder="-" />
        </label>
        {/* Set apart by a hairline: this is not a third PRICE, it is a
            deduction from the one before it. */}
        <label className="dish-price-field dish-price-field--sep">
          <span>עמלת וולט (%)</span>
          <input className="form-control input-money" type="number" min="0" max="100" step="0.1" inputMode="decimal"
            style={{ direction: 'ltr', textAlign: 'right' }}
            value={woltPct} onChange={e => setWoltPct(e.target.value)} onBlur={onWoltPctBlur} placeholder="-" />
        </label>
      </div>

      {dish.ingredients.length === 0 ? (
        <EmptyState message="עדיין אין מרכיבים. הוסיפו מרכיב כדי להתחיל לחשב את עלות המנה.">
          {/* The only add affordance on an empty dish. "+ הוסף מרכיב" now lives
              in the ⋮ menu, which is fine once there is a table to act on and
              wrong when the page is blank - nothing would tell a first-time
              user where to start. */}
          <button type="button" className="empty-action" onClick={openAdd}>הוסף מרכיב ראשון</button>
        </EmptyState>
      ) : isMobile ? (
        /* An ingredient row is an EDITOR — quantity field, conversion control,
           delete - so it cannot be a ListRow: that is a <button>, and neither an
           input nor a nested button may live inside one. Same two-line anatomy,
           built as a plain container. */
        <>
        {/* No button here: on a phone the page ⋮ carries "הוסף מרכיב". */}
        <div className="m-list dish-ing-list">
          {rankedIngredients.map(ing => (
            <div key={ing.id} className="dish-ing-card">
              <div className="dish-ing-head">
                <div className="dish-ing-name">
                  <div>{ing.product_name}</div>
                  <div className="dish-ing-supplier">{ing.supplier_name}<HqOnlyLock hqOnly={ing.hq_only} /></div>
                </div>
                <div className="dish-ing-cost">
                  {numCell(ing) || formatDishCurrency(ing.cost)}
                  <div className="dish-ing-share"><ShareCell ing={ing} total={dish.total_cost} /></div>
                </div>
              </div>
              <div className="dish-ing-controls">
                <label className="dish-ing-qty">
                  <span className="dish-ing-qty-label">כמות</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input className="form-control" type="number" min="0" step="any" inputMode="decimal"
                      style={{ direction: 'ltr', textAlign: 'right', width: 90, padding: '6px 8px' }}
                      /* Keyed on the PERSISTED value: the field is uncontrolled, so
                         defaultValue applies only on mount — and the server clamps
                         (Math.max(0, …)), so a rejected entry would otherwise stay on
                         screen while every figure was computed from the stored number.
                         Changing the key remounts the input with what was actually
                         saved; safe here because the save runs on blur, so focus has
                         already left the field. */
                      key={`qty-${ing.id}-${ing.qty_used}`}
                      defaultValue={ing.qty_used || ''}
                      onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== ing.qty_used) saveIngredient(ing, { qty_used: v }); }} />
                    <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>{UNIT_LABEL[ing.base_unit] || UNIT_LABEL.gram}</span>
                  </span>
                </label>
                <div className="dish-ing-actions">
                  {ing.needs_costing ? (
                    <button className="btn btn-outline btn-sm" onClick={() => openCosting(ing)}>הגדר המרה</button>
                  ) : (
                    <button type="button" className="cell-link" onClick={() => openCosting(ing)}>
                      {ing.pack_label || fmtQty(ing.pack_qty, ing.base_unit)}
                      {ing.waste_pct > 0 ? ` · פחת ${ing.waste_pct}%` : ''} <Icon name="edit" size={13} />
                    </button>
                  )}
<div className="page-menu">
                      <button type="button" className="page-menu-trigger"
                        aria-label={`פעולות עבור ${ing.product_name}`}
                        aria-expanded={ingMenuFor === ing.id}
                        onClick={() => setIngMenuFor(cur => (cur === ing.id ? null : ing.id))}
                      >⋮</button>
                      {ingMenuFor === ing.id && (
                        <>
                          <div className="menu-backdrop" onClick={() => setIngMenuFor(null)} />
                          <div className="page-menu-panel" style={{ minWidth: 180 }}>
                            <button type="button" className="page-menu-item"
                              onClick={() => { setIngMenuFor(null); openCosting(ing); }}>
                              <Icon name="edit" size={15} /> {ing.needs_costing ? 'הגדר המרה' : 'ערוך המרה'}
                            </button>
                            <button type="button" className="page-menu-item" style={{ color: '#b91c1c' }}
                              onClick={() => { setIngMenuFor(null); removeIngredient(ing); }}>
                              <Icon name="trash" size={15} /> הסר מהמנה
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="dish-sort-note dish-sort-note--mobile">{SORT_NOTE}</p>
        </>
      ) : (
        <div className="card">
          {/* The card's own header row. The button lived OUTSIDE the card,
              floating above a surface it belongs to - the same problem the view
              tabs had, and solved the same way: .card is borderless with an
              all-round shadow, so nothing placed on top of it can be made to
              look joined to it. Inside, opposite the caption, it reads as this
              table's action.
              The caption is not decoration: rows are sorted by cost and a
              quantity edit reloads them, so a row can MOVE after you type in
              it. A table that silently reorders itself reads as a bug unless it
              says what it is doing. */}
          <div className="dish-table-head">
            <button className="btn btn-sm btn-primary" onClick={openAdd}>+ הוסף מרכיב</button>
          </div>
          <table>
            <thead>
              <tr>
                <th>מרכיב</th>
                <th>ספק</th>
                <th>המרה</th>
                <th>כמות</th>
                <th>עלות</th>
                <th title="חלקו של המרכיב מעלות המנה כולה. השורות מסודרות מהיקר לזול; מרכיבים שאינם מתומחרים מופיעים ראשונים.">חלק מהעלות</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rankedIngredients.map(ing => (
                <tr key={ing.id}>
                  <td>{ing.product_name}</td>
                  <td>{ing.supplier_name}<HqOnlyLock hqOnly={ing.hq_only} /></td>
                  <td>
                    {ing.needs_costing ? (
                      <button className="btn btn-outline btn-sm" onClick={() => openCosting(ing)}>הגדר המרה</button>
                    ) : (
                      <button type="button" className="cell-link" onClick={() => openCosting(ing)}
                        title="ערוך המרה (יחידה, כמות באריזה, פחת)">
                        {ing.pack_label || fmtQty(ing.pack_qty, ing.base_unit)}
                        {ing.waste_pct > 0 ? ` · פחת ${ing.waste_pct}%` : ''} <Icon name="edit" size={13} />
                      </button>
                    )}
                  </td>
                  <td>
                    {/* The unit sits next to the input on purpose: reading it off
                        the המרה column instead is what let a 0.01 slip in for a 10. */}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input className="form-control" type="number" min="0" step="any" inputMode="decimal"
                        style={{ direction: 'ltr', textAlign: 'right', width: 90, padding: '6px 8px' }}
                        /* Keyed on the PERSISTED value: the field is uncontrolled, so
                           defaultValue applies only on mount — and the server clamps
                           (Math.max(0, …)), so a rejected entry would otherwise stay on
                           screen while every figure was computed from the stored number.
                           Changing the key remounts the input with what was actually
                           saved; safe here because the save runs on blur, so focus has
                           already left the field. */
                        key={`qty-${ing.id}-${ing.qty_used}`}
                        defaultValue={ing.qty_used || ''}
                        onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== ing.qty_used) saveIngredient(ing, { qty_used: v }); }} />
                      <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>{UNIT_LABEL[ing.base_unit] || UNIT_LABEL.gram}</span>
                    </span>
                  </td>
                  <td style={{ fontWeight: 700 }}>{numCell(ing) || formatDishCurrency(ing.cost)}</td>
                  <td><ShareCell ing={ing} total={dish.total_cost} /></td>
                  {/* Same rule as the dish rows on /dishes: a destructive
                      action does not sit at rest on every line. */}
                  <td>
                    <div className="page-menu">
                      <button type="button" className="page-menu-trigger"
                        aria-label={`פעולות עבור ${ing.product_name}`}
                        aria-expanded={ingMenuFor === ing.id}
                        onClick={() => setIngMenuFor(cur => (cur === ing.id ? null : ing.id))}
                      >⋮</button>
                      {ingMenuFor === ing.id && (
                        <>
                          <div className="menu-backdrop" onClick={() => setIngMenuFor(null)} />
                          <div className="page-menu-panel" style={{ minWidth: 180 }}>
                            <button type="button" className="page-menu-item"
                              onClick={() => { setIngMenuFor(null); openCosting(ing); }}>
                              <Icon name="edit" size={15} /> {ing.needs_costing ? 'הגדר המרה' : 'ערוך המרה'}
                            </button>
                            <button type="button" className="page-menu-item" style={{ color: '#b91c1c' }}
                              onClick={() => { setIngMenuFor(null); removeIngredient(ing); }}>
                              <Icon name="trash" size={15} /> הסר מהמנה
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Under the rows, not above them: it describes an order you can only
              notice once you have seen the rows, and a caption above the column
              headers competed with them for the same line. */}
          <p className="dish-sort-note dish-sort-note--foot">{SORT_NOTE}</p>
        </div>
      )}

      {/* Add-ingredient modal */}
      {/* closeOnBackdrop={false}: the form holds a picked supplier, product,
          quantity and possibly a new conversion - a stray backdrop click must
          not discard all of it. */}
      <Modal open={showAdd} onClose={closeAdd} title="הוסף מרכיב" closeOnBackdrop={false}>
            <form onSubmit={handleAddIngredient}>
              <div className="form-group">
                <label>ספק *</label>
                <SearchSelect
                  value={supplierId}
                  placeholder="הקלידו או בחרו ספק…"
                  options={suppliers.map(s => ({ id: s.id, label: s.name }))}
                  onChange={(sid) => onPickSupplier(sid)}
                />
              </div>
              <div className="form-group">
                <label>מוצר *</label>
                <SearchSelect
                  value={productId}
                  disabled={!supplierId}
                  placeholder={supplierId ? 'הקלידו או בחרו מוצר…' : 'בחרו ספק תחילה'}
                  options={products.map(p => ({
                    id: p.id,
                    label: p.last_price > 0 ? `${p.name} - ${formatCurrency(p.last_price)}` : p.name,
                  }))}
                  onChange={onPickProduct}
                />
              </div>
              {/* The conversion, right here in the add flow. A conversion belongs
                  to the PRODUCT and is shared by every dish that uses it, so an
                  existing one is read-only: editing it from inside one dish would
                  move other dishes' costs with no warning. When the lookup FAILED
                  we show nothing at all - offering to create a conversion for a
                  product whose real one we could not read is how a PUT ends up
                  overwriting it. */}
              {productId && (addCostingError ? (
                <div className="form-group" style={{ background: '#f7f9fb', border: '1px solid #e8ecf0', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.82rem', color: '#b91c1c' }}>לא ניתן היה לטעון את ההמרה של המוצר</div>
                  <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 8 }}
                    onClick={() => onPickProduct(productId)}>נסה שוב</button>
                </div>
              ) : addCostingLoading ? (
                <div className="form-group" style={{ background: '#f7f9fb', border: '1px solid #e8ecf0', borderRadius: 10, padding: 12, fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
                  טוען המרה…
                </div>
              ) : addCosting ? (
                <div className="form-group" style={{ background: '#f7f9fb', border: '1px solid #e8ecf0', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.82rem' }}>
                    חבילה: <strong>{addCosting.pack_label || fmtQty(addCosting.pack_qty, addCosting.base_unit)}</strong>
                    {addCosting.waste_pct > 0 ? ` · פחת ${addCosting.waste_pct}%` : ''}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 4 }}>
                    ההמרה מוגדרת על המוצר ומשותפת לכל המנות. לשינוי - דרך עמודת ״המרה״ בשורת המרכיב.
                  </div>
                </div>
              ) : (
                <div className="form-group" style={{ background: '#f7f9fb', border: '1px solid #e8ecf0', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 8 }}>הגדרת המרה - פעם אחת למוצר</div>
                  <ConversionFields
                    baseUnit={addBaseUnit} onBaseUnitChange={setAddBaseUnit}
                    packQty={addPackQty} onPackQtyChange={setAddPackQty}
                    wastePct={addWastePct} onWastePctChange={setAddWastePct}
                    idPrefix="add-ingredient"
                  />
                  <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 8 }}>
                    אפשר להשאיר ריק ולהגדיר אחר כך - המרכיב יתווסף עם כפתור "הגדר המרה".
                  </div>
                </div>
              ))}

              <div className="form-group">
                <label>{addCostingError ? 'כמות במנה' : `כמות במנה (${UNIT_LABEL[addUnit] || UNIT_LABEL.gram})`}</label>
                <input className="form-control" type="number" min="0" step="any" inputMode="decimal"
                  style={{ direction: 'ltr', textAlign: 'right' }}
                  value={qtyUsed} onChange={e => setQtyUsed(e.target.value)}
                  placeholder={addUnit === 'unit' ? 'לדוגמה: 1' : 'לדוגמה: 150'} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeAdd}>ביטול</button>
                <button type="submit" className="btn btn-primary" disabled={saving || addCostingLoading} aria-busy={saving}>הוסף</button>
              </div>
            </form>
      </Modal>

      {/* Conversion modal */}
      <Modal open={!!costingRow} onClose={() => setCostingRow(null)} closeOnBackdrop={false}
        title={costingRow ? `המרת יחידה - ${costingRow.product_name}` : ''}>
        {costingRow && (<>
            <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginBottom: 18 }}>
              המחיר הנוכחי {costingRow.last_price != null ? formatCurrency(costingRow.last_price) : '(לא ידוע)'} הוא ליחידת קנייה אחת.
              כמה {baseUnit === 'unit' ? 'יחידות' : 'גרם'} יש ביחידה הזו? (ההגדרה נשמרת פעם אחת ותשמש בכל מנה שמשתמשת במוצר.)
            </p>

            {/* "How it's bought today" - recent invoice lines so the user can
                infer the unit without digging through documents. */}
            {loadingPurchases ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 16 }}>טוען היסטוריית קנייה…</div>
            ) : purchases.length > 0 ? (
              <div style={{ background: '#f7f9fb', border: '1px solid #e8ecf0', borderRadius: 10, padding: 12, marginBottom: 18 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>כך נקנה לאחרונה</div>
                <table style={{ fontSize: '0.8rem', width: '100%' }}>
                  <thead>
                    <tr><th>תאריך</th><th>תיאור בחשבונית</th><th>כמות</th><th>מחיר ליח׳</th><th>מסמך</th></tr>
                  </thead>
                  <tbody>
                    {purchases.map((p, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateShort(p.date)}</td>
                        <td>{p.description}</td>
                        <td style={{ direction: 'ltr', textAlign: 'right' }}>{p.qty}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatCurrency(p.unit_price)}</td>
                        <td>
                          <button type="button" className="cell-link"
                            title="פתח את המסמך המקורי בלשונית חדשה"
                            onClick={() => window.open(tenantUrl(restaurantId, `/document/${p.document_id}`), '_blank', 'noopener')}>
                            <Icon name="external" size={14} /> פתח
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 8 }}>
                  פתחו את המסמך כדי לראות איך המוצר נמכר בפועל (אריזה / משקל).
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 16 }}>
                אין היסטוריית קנייה זמינה למוצר זה.
              </div>
            )}

            <form onSubmit={handleSaveCosting}>
              {/* Counted products (a bun, an egg) have no sensible weight to
                  enter, so they keep their own base unit. Everything weighed is
                  grams - no unit picker, which is what let units drift apart. */}
              <ConversionFields
                baseUnit={baseUnit} onBaseUnitChange={setBaseUnit}
                packQty={packQty} onPackQtyChange={setPackQty}
                wastePct={costWastePct} onWastePctChange={setCostWastePct}
                idPrefix="costing" autoFocus
              />
              <div className="form-group">
                <label>תיאור אריזה (לא חובה)</label>
                <input className="form-control" value={packLabel}
                  onChange={e => setPackLabel(e.target.value)} placeholder='לדוגמה: ארגז 6 ק"ג' />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setCostingRow(null)}>ביטול</button>
                <button type="submit" className="btn btn-primary" disabled={saving} aria-busy={saving}>שמור המרה</button>
              </div>
            </form>
        </>)}
      </Modal>

      {/* One place that explains the whole screen. The metric cards each carry
          their own ⓘ, which answers "what is THIS number" while you are looking
          at it; this answers "what is this screen" - including the table
          columns, which have no room for a ⓘ of their own. */}
      <Modal open={showHelp} onClose={() => setShowHelp(false)} size="lg" title="חשוב לדעת">
        <p className="dish-help-lead">
          המסך הזה מראה כמה עולה לכם להכין מנה אחת, וכמה נשאר מכל מנה שנמכרת.
          העלות מחושבת מהמחיר האחרון ששילמתם על כל מרכיב.
        </p>

        <div className="dish-help-section">הכרטיסים למעלה</div>
        <dl className="dish-help-list">
          <dt>עלות מנה</dt>
          <dd>כמה עולים חומרי הגלם של מנה אחת - סכום העלות של כל המרכיבים.</dd>

          <dt>עלות מזון % - תפריט</dt>
          <dd>איזה חלק ממחיר המנה בתפריט הולך על חומרי הגלם. ככל שנמוך יותר, נשאר לכם יותר. מתחתיו מופיע הרווח הגולמי בשקלים.</dd>

          <dt>עלות מזון % - וולט</dt>
          <dd>אותו חישוב מול המחיר שאתם גובים בוולט. חומרי הגלם עולים אותו דבר; המחיר בוולט גבוה יותר, ולכן האחוז יוצא נמוך יותר. עמלת וולט יורדת מהרווח, לא מהעלות - לכן אחוז נמוך יותר לא אומר שהמנה רווחית יותר שם.</dd>

          <dt>משקל מנה</dt>
          <dd>סך המשקל של המרכיבים. מוצרים שנספרים ביחידות (ביצה, לחמנייה) לא נכללים - אין להם משקל.</dd>
        </dl>

        <div className="dish-help-section">העמודות בטבלה</div>
        <dl className="dish-help-list">
          <dt>מרכיב · ספק</dt>
          <dd>המוצר מהקטלוג שנכנס למנה, והספק שממנו הוא נקנה.</dd>

          <dt>המרה</dt>
          <dd>כמה גרם (או יחידות) יש ביחידת הקנייה. זה מה שמאפשר להפוך מחיר של ארגז למחיר של גרם. מוגדר פעם אחת למוצר ומשמש בכל המנות שמשתמשות בו. אפשר להוסיף כאן גם פחת.</dd>

          <dt>כמות</dt>
          <dd>כמה נכנס למנה, ביחידות הבסיס. זה השדה שאתם עורכים.</dd>

          <dt>עלות</dt>
          <dd>כמה עולה המרכיב הזה במנה אחת: המחיר ליחידת בסיס כפול הכמות.</dd>

          <dt>חלק מהעלות</dt>
          <dd>איזה חלק מעלות המנה מגיע מהמרכיב הזה. השורות מסודרות מהיקר לזול, כדי שיהיה ברור מיד מה מייקר את המנה.</dd>
        </dl>

        <div className="dish-help-example">
          <div className="dish-help-example-title">דוגמה</div>
          <p>מנה שמחירה בתפריט ₪48, ונכנסים אליה 200 גרם עגבניות. ארגז עגבניות של 1,000 גרם עלה ₪10.</p>
          <ul>
            <li>המרה: ‎1,000 גרם בארגז</li>
            <li>מחיר לגרם: ‎10 ÷ 1,000 = <strong>₪0.01</strong></li>
            <li>עלות המרכיב: ‎0.01 × 200 = <strong>₪2.00</strong></li>
            <li>עלות מזון %: ‎2 ÷ 48 = <strong>4.2%</strong></li>
            <li>רווח גולמי: ‎48 − 2 = <strong>₪46.00</strong></li>
          </ul>
        </div>

        <p className="dish-help-note">
          מרכיב שאין לו המרה או מחיר נספר כאפס. במצב כזה העלות נמוכה מהאמיתית, ולכן אחוז עלות המזון והרווח הגולמי לא מוצגים בכלל - עדיף בלי מספר מאשר מספר שמחמיא.
        </p>

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={() => setShowHelp(false)}>הבנתי</button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}
